/*
 * Translations. Turkish source text is the key (gettext style): t('Kaydet')
 * returns the text in the current language, or the source when there is no
 * translation. Values are passed positionally: t('{0} sayfa', n).
 *
 * The current language comes from a provider so the server can answer each
 * request in its own language (AsyncLocalStorage) while the browser uses one
 * global setting.
 */

export interface LangInfo {
  code: string;
  /** name in the language itself */
  name: string;
  rtl?: boolean;
}

/** Turkish first, then the most widely used languages. */
export const LANGS: LangInfo[] = [
  { code: 'tr', name: 'Türkçe' },
  { code: 'en', name: 'English' },
  { code: 'zh', name: '中文' },
  { code: 'hi', name: 'हिन्दी' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'ar', name: 'العربية', rtl: true },
  { code: 'pt', name: 'Português' },
  { code: 'ru', name: 'Русский' },
  { code: 'de', name: 'Deutsch' },
  { code: 'ja', name: '日本語' },
];
export const SOURCE_LANG = 'tr';
export type Dict = Record<string, string>;

const dicts = new Map<string, Dict>();
let current = SOURCE_LANG;
let provider: (() => string) | null = null;

export const isLang = (c: unknown): c is string => typeof c === 'string' && LANGS.some((l) => l.code === c);

/** Registers the translations of a language (loaded by the host). */
export function addDict(code: string, d: Dict) {
  dicts.set(code, { ...dicts.get(code), ...d });
}
export const hasDict = (code: string) => code === SOURCE_LANG || dicts.has(code);

export function setLang(code: string) {
  current = isLang(code) ? code : SOURCE_LANG;
}
/** Server: language of the running request. */
export function setLangProvider(f: (() => string) | null) {
  provider = f;
}
export const getLang = () => provider?.() ?? current;

/** Picks a supported language from a browser/Accept-Language list, e.g. "de-DE,de;q=0.9,en;q=0.8". */
export function pickLang(accept: string | readonly string[] | undefined, fallback = SOURCE_LANG): string {
  const list = typeof accept === 'string' ? accept.split(',').map((x) => x.split(';')[0].trim()) : [...(accept ?? [])];
  for (const a of list) {
    const c = a.toLowerCase().split('-')[0];
    if (isLang(c)) return c;
  }
  return fallback;
}

const fill = (s: string, v: unknown[]) => (v.length ? s.replace(/\{(\d+)\}/g, (m, i) => (+i < v.length ? String(v[+i]) : m)) : s);

/** Translates Turkish source text; `{0}`, `{1}` … are replaced by the values. */
export function t(src: string, ...v: unknown[]): string {
  const lang = getLang();
  const tr = lang === SOURCE_LANG ? undefined : dicts.get(lang)?.[src];
  return fill(tr || src, v);
}

/** Placeholders and markup a translation must keep: {0}…, HTML tags with their attributes. */
export function translationShape(s: string): string[] {
  const ph = s.match(/\{\d+\}/g) ?? [];
  // attribute texts people read (title, placeholder…) may be translated; everything else must stay
  const tags = (s.match(/<\/?[a-z][a-z0-9]*\b[^>]*>/gi) ?? []).map((x) =>
    x.replace(/\s(title|placeholder|aria-label|alt)="[^"]*"/g, ' $1=""').replace(/\s+/g, ' '),
  );
  return [...ph, ...tags].sort();
}

/** Marks text for translation without translating it now (module-level constants); translate where shown with t(). */
export const tk = (src: string) => src;

/** The translations of a language (empty for Turkish or when not loaded). */
export const dictOf = (code: string): Dict => dicts.get(code) ?? {};
